import React, { useEffect, useState } from "react";
import { Button } from "./ui/button";
import {
  Star,
  TrendingUp,
  Users,
  Zap,
  Mail,
  Link2,
  CheckCircle,
  Trophy,
  Globe,
  Briefcase,
  UserCheck,
  BarChart3,
  DollarSign,
  Receipt,
  Shield,
  Route,
  Building2,
  FileText,
  Activity,
} from "lucide-react";
import Footer from "./Footer";
import Header from "./Header";
import WhatsAppFloat from "./WhatsAppFloat";
import FAQSchema from "./FAQSchema";
import { useLocation } from "react-router-dom";
import { URL, LINKEDIN_URL, INSTAGRAM_URL, FACEBOOK_URL } from "./constant";
import { useTranslation } from "react-i18next";

export default function Home() {
  const location = useLocation();
  const { t } = useTranslation('home');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [stats, setStats] = useState({
    families: 0,
    countries: 0,
    locationChecks: 0
  });
  const [spinIndex, setSpinIndex] = useState(0);
  const [spinVisible, setSpinVisible] = useState(true);
  const spins = t('hero.headlineSpins', { returnObjects: true }) || [];

  // Add custom styles for animations
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fade-in {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .animate-fade-in {
        animation: fade-in 0.8s ease-out forwards;
      }
      @keyframes float {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-20px); }
      }
      .animate-float {
        animation: float 6s ease-in-out infinite;
      }
      @keyframes spin-in {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes spin-out {
        from { opacity: 1; transform: translateY(0); }
        to { opacity: 0; transform: translateY(-20px); }
      }
      .spin-in { animation: spin-in 0.5s ease-out forwards; }
      .spin-out { animation: spin-out 0.4s ease-in forwards; }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Headline spin rotation
  useEffect(() => {
    if (!spins.length) return;
    const interval = setInterval(() => {
      setSpinVisible(false);
      setTimeout(() => {
        setSpinIndex(prev => (prev + 1) % spins.length);
        setSpinVisible(true);
      }, 400);
    }, 3500);
    return () => clearInterval(interval);
  }, [spins.length]);

  // Animated counter effect
  useEffect(() => {
    const animateValue = (key, start, end, duration) => {
      const range = end - start;
      const increment = range / (duration / 16);
      let current = start;

      const timer = setInterval(() => {
        current += increment;
        if (current >= end) {
          current = end;
          clearInterval(timer);
        }
        setStats(prev => ({...prev, [key]: Math.floor(current)}));
      }, 16);
    };

    const timeout = setTimeout(() => {
      animateValue('families', 0, 50000, 2000);
      animateValue('countries', 0, 195, 2000);
      animateValue('locationChecks', 0, 1000000, 2500);
    }, 500);

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    // Intersection Observer for scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("animate-in");
        }
      });
    }, observerOptions);

    // Observe all animated elements
    const animatedElements = document.querySelectorAll(".scroll-animate");
    animatedElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prepare mailto link with form data
    const subject = encodeURIComponent(`Contact from ${name.trim()}`);
    const body = encodeURIComponent(
      `Name: ${name.trim()}\nEmail: ${email.trim()}\n\nMessage:\n${message.trim()}`
    );
    const mailtoLink = `mailto:support@trackteam.app?subject=${subject}&body=${body}`;

    // Open mail app
    window.location.href = mailtoLink;

    // Show success message and reset form
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
      setName("");
      setEmail("");
      setMessage("");
    }, 800);
  };

  // Auto-hide success after 3 seconds
  useEffect(() => {
    if (submitted) {
      const timer = setTimeout(() => setSubmitted(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [submitted]);

  // Scroll to contact when URL has #contact
  useEffect(() => {
    if (location.hash === '#contact') {
      const el = document.getElementById('contact');
      if (el) {
        const y = el.getBoundingClientRect().top + window.pageYOffset - 100;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      {/* Hero Section */}
      <section className="relative min-h-[85vh] md:min-h-[90vh] pt-20 md:pt-28 flex items-center overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/40 to-purple-50/30">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-400/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>

        <div className="relative w-full flex flex-col md:flex-row items-center md:items-center justify-center md:justify-between gap-8 md:gap-12 px-4 sm:px-6 md:px-12 lg:px-16">
          {/* Left Section - Text and Button */}
          <div className="flex-[1.7] md:max-w-4xl max-w-3xl text-center md:text-left z-10">
            {/* Trust Badge */}
            <div className="inline-flex items-center gap-3 bg-white rounded-full px-5 py-3 border border-gray-200 shadow-lg shadow-purple-100/50 mb-8 hover:shadow-xl transition-all duration-300 hover:scale-105">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-semibold text-gray-800">{t('hero.trustBadge')}</span>
              </div>
              <div className="flex items-center gap-1">
                <Shield className="w-4 h-4 text-green-600" />
                <span className="text-xs text-green-600 font-medium">{t('hero.secure')}</span>
              </div>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-gray-900 mb-6 md:mb-8 leading-[1.1] text-left">
              <div className="mb-3 animate-fade-in">{t('hero.headlineStatic')}</div>
              <div className="h-[1.2em] overflow-hidden">
                <div
                  style={{fontSize: '0.75em', lineHeight: '1.2'}}
                  key={spinIndex}
                  className={`bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent ${spinVisible ? 'spin-in' : 'spin-out'}`}
                >
                  {spins[spinIndex]}
                </div>
              </div>
            </h1>

            <p className="text-xl md:text-2xl text-gray-600 mb-10 md:mb-12 leading-relaxed text-left max-w-2xl font-light">
              {t('hero.subheadline')}
            </p>

            <div className="flex flex-col sm:flex-row gap-5 items-start">
              <div className="flex items-center gap-4">
                <a
                  href="https://apps.apple.com/ae/app/trackteam/id6755401884"
                  target="_blank"
                  rel="noreferrer"
                  aria-label={t('hero.appStoreAlt')}
                  className="inline-flex transition-transform hover:-translate-y-1 hover:scale-105"
                >
                  <img
                    src="/Image/logo_appstore.svg"
                    alt={t('hero.appStoreAlt')}
                    className="h-12 sm:h-12 md:h-14 w-[162px] sm:w-[162px] md:w-[189px] object-contain"
                    loading="lazy"
                  />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.washey.familytrack.trackteam&pcampaignid=web_share"
                  target="_blank"
                  rel="noreferrer"
                  aria-label={t('hero.playStoreAlt')}
                  className="inline-flex transition-transform hover:-translate-y-1 hover:scale-105"
                >
                  <img
                    src="/Image/logo_playstore.svg"
                    alt={t('hero.playStoreAlt')}
                    className="h-12 sm:h-12 md:h-14 w-[162px] sm:w-[162px] md:w-[189px] object-contain"
                    loading="lazy"
                  />
                </a>
              </div>
            </div>
          </div>

          {/* Right Section - App Preview */}
          <div className="flex-1 relative flex justify-center md:justify-end mt-12 md:mt-0">
            <div className="relative w-full max-w-[450px] md:max-w-[550px]">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-600 rounded-3xl blur-3xl opacity-20 animate-pulse"></div>
              <div className="relative animate-float">
                <img
                  src="/TrackTeam/section1.png"
                  alt={t('hero.heroImageAlt')}
                  className="w-full h-auto object-contain drop-shadow-2xl transform hover:scale-105 transition-transform duration-500"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="relative bg-white py-16 md:py-24 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center scroll-animate">
          <div className="inline-flex items-center justify-center rounded-full bg-[#62329f] text-white font-semibold px-6 py-3 border-2 border-black shadow-[4px_4px_0_#000] mb-4">
            {t('howItWorks.badge')}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
            {t('howItWorks.title')}
          </h2>
          <p className="mt-2 md:mt-4 text-gray-600 text-base md:text-lg max-w-2xl mx-auto">
            {t('howItWorks.subtitle')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-8 mt-8 md:mt-12 mb-8 md:mb-20">
            {/* Card 1 */}
            <div className="relative scroll-animate group">
              <div className="absolute inset-0 translate-x-2 translate-y-2 bg-[#62329f]/30 rounded-3xl -z-10"></div>
              <div className="bg-white rounded-3xl border-4 border-black overflow-hidden transition-transform hover:-translate-x-1 hover:-translate-y-1">
                <div className="absolute top-6 left-6 z-10 w-12 h-12 rounded-full bg-[#62329f] border-3 border-black flex items-center justify-center shadow-lg">
                  <span className="text-2xl font-bold text-white">1</span>
                </div>
                <div className="relative bg-gradient-to-br from-blue-50 to-blue-100 p-8 pt-20">
                  <img
                    src="/TrackTeam/3905531.jpg"
                    alt={t('howItWorks.step1.title')}
                    className="w-full h-64 object-contain rounded-2xl"
                    loading="lazy"
                  />
                </div>
                <div className="p-8 bg-white">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 border-2 border-black flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-black">
                      {t('howItWorks.step1.title')}
                    </h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    {t('howItWorks.step1.desc')}
                  </p>
                </div>
              </div>
            </div>

            {/* Card 2 */}
            <div className="relative scroll-animate group">
              <div className="absolute inset-0 translate-x-2 translate-y-2 bg-[#62329f]/30 rounded-3xl -z-10"></div>
              <div className="bg-white rounded-3xl border-4 border-black overflow-hidden transition-transform hover:-translate-x-1 hover:-translate-y-1">
                <div className="absolute top-6 left-6 z-10 w-12 h-12 rounded-full bg-[#62329f] border-3 border-black flex items-center justify-center shadow-lg">
                  <span className="text-2xl font-bold text-white">2</span>
                </div>
                <div className="relative bg-gradient-to-br from-green-50 to-green-100 p-8 pt-20">
                  <img
                    src="/TrackTeam/2672292.jpg"
                    alt={t('howItWorks.step2.title')}
                    className="w-full h-64 object-contain rounded-2xl"
                    loading="lazy"
                  />
                </div>
                <div className="p-8 bg-white">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 border-2 border-black flex items-center justify-center">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-black">
                      {t('howItWorks.step2.title')}
                    </h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    {t('howItWorks.step2.desc')}
                  </p>
                </div>
              </div>
            </div>

            {/* Card 3 */}
            <div className="relative scroll-animate group">
              <div className="absolute inset-0 translate-x-2 translate-y-2 bg-[#62329f]/30 rounded-3xl -z-10"></div>
              <div className="bg-white rounded-3xl border-4 border-black overflow-hidden transition-transform hover:-translate-x-1 hover:-translate-y-1">
                <div className="absolute top-6 left-6 z-10 w-12 h-12 rounded-full bg-[#62329f] border-3 border-black flex items-center justify-center shadow-lg">
                  <span className="text-2xl font-bold text-white">3</span>
                </div>
                <div className="relative bg-gradient-to-br from-orange-50 to-orange-100 p-8 pt-20">
                  <img
                    src="/TrackTeam/preview.jpg"
                    alt={t('howItWorks.step3.title')}
                    className="w-full h-64 object-contain rounded-2xl"
                    loading="lazy"
                  />
                </div>
                <div className="p-8 bg-white">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 border-2 border-black flex items-center justify-center">
                      <BarChart3 className="w-6 h-6 text-white" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-black">
                      {t('howItWorks.step3.title')}
                    </h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    {t('howItWorks.step3.desc')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Decorative wave - bottom right */}
        <svg
          className="hidden md:block pointer-events-none select-none absolute -bottom-4 right-0 w-[648px] h-[204px]"
          viewBox="0 0 540 170"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="hiwWave" x1=" 0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#62329f" />
              <stop offset="100%" stopColor="#62329f" />
            </linearGradient>
          </defs>
          <path
            d="M0 150 C 40 120, 90 140, 130 130 C 170 75, 220 65, 260 115 C 300 155, 340 130, 380 70 C 430 0, 490 5, 540 45 L 540 170 L 0 170 Z"
            fill="url(#hiwWave)"
            stroke="#111827"
            strokeWidth="2"
          />
        </svg>
      </section>

      {/* Trust Statistics Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 py-20 md:py-28">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full bg-grid-white/[0.05] bg-[size:30px_30px]"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-4">
              <Trophy className="w-4 h-4 text-yellow-300" />
              <span className="text-white text-sm font-medium">{t('stats.industryLeading')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t('stats.title')}
            </h2>
            <p className="text-blue-100 text-xl max-w-2xl mx-auto">
              {t('stats.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Stat 1 */}
            <div className="relative scroll-animate group">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-blue-600 rounded-3xl blur-xl opacity-20 group-hover:opacity-40 transition-opacity"></div>
              <div className="relative bg-white rounded-3xl p-10 text-center transition-all duration-500 hover:scale-105 hover:shadow-2xl">
                <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-lg shadow-blue-500/50 transform group-hover:rotate-6 transition-transform">
                  <Building2 className="w-10 h-10 text-white" />
                </div>
                <div className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-3">
                  {stats.families.toLocaleString()}+
                </div>
                <div className="text-gray-600 font-medium text-lg">
                  {t('stats.teamsManaged')}
                </div>
              </div>
            </div>

            {/* Stat 2 */}
            <div className="relative scroll-animate group">
              <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-emerald-600 rounded-3xl blur-xl opacity-20 group-hover:opacity-40 transition-opacity"></div>
              <div className="relative bg-white rounded-3xl p-10 text-center transition-all duration-500 hover:scale-105 hover:shadow-2xl">
                <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-700 flex items-center justify-center shadow-lg shadow-green-500/50 transform group-hover:rotate-6 transition-transform">
                  <Globe className="w-10 h-10 text-white" />
                </div>
                <div className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-3">
                  {stats.countries}+
                </div>
                <div className="text-gray-600 font-medium text-lg">
                  {t('stats.countries')}
                </div>
              </div>
            </div>

            {/* Stat 3 */}
            <div className="relative scroll-animate group">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-400 to-red-600 rounded-3xl blur-xl opacity-20 group-hover:opacity-40 transition-opacity"></div>
              <div className="relative bg-white rounded-3xl p-10 text-center transition-all duration-500 hover:scale-105 hover:shadow-2xl">
                <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center shadow-lg shadow-orange-500/50 transform group-hover:rotate-6 transition-transform">
                  <Activity className="w-10 h-10 text-white" />
                </div>
                <div className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent mb-3">
                  {(stats.locationChecks / 1000000).toFixed(1)}M+
                </div>
                <div className="text-gray-600 font-medium text-lg">
                  {t('stats.visitsTracked')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative bg-gradient-to-b from-white via-gray-50 to-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center scroll-animate mb-16">
            <div className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold px-6 py-3 shadow-lg shadow-purple-500/30 mb-6">
              <Zap className="w-4 h-4 mr-2" />
              {t('features.badge')}
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('features.title')}
            </h2>
            <p className="text-gray-600 text-xl max-w-3xl mx-auto leading-relaxed">
              {t('features.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="relative scroll-animate group">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-blue-600 rounded-3xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-500"></div>
              <div className="relative bg-white rounded-3xl p-8 h-full transition-all duration-500 hover:scale-105 hover:shadow-2xl border border-gray-100">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mb-6 shadow-lg shadow-blue-500/30 transform group-hover:rotate-6 transition-transform duration-500">
                  <Route className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
                  {t('features.location.title')}
                </h3>
                <p className="text-gray-600 text-base leading-relaxed">
                  {t('features.location.desc')}
                </p>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="relative scroll-animate group">
              <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-emerald-600 rounded-3xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-500"></div>
              <div className="relative bg-white rounded-3xl p-8 h-full transition-all duration-500 hover:scale-105 hover:shadow-2xl border border-gray-100">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mb-6 shadow-lg shadow-green-500/30 transform group-hover:rotate-6 transition-transform duration-500">
                  <Receipt className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
                  {t('features.expense.title')}
                </h3>
                <p className="text-gray-600 text-base leading-relaxed">
                  {t('features.expense.desc')}
                </p>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="relative scroll-animate group">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-400 to-red-600 rounded-3xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-500"></div>
              <div className="relative bg-white rounded-3xl p-8 h-full transition-all duration-500 hover:scale-105 hover:shadow-2xl border border-gray-100">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center mb-6 shadow-lg shadow-orange-500/30 transform group-hover:rotate-6 transition-transform duration-500">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
                  {t('features.tracking.title')}
                </h3>
                <p className="text-gray-600 text-base leading-relaxed">
                  {t('features.tracking.desc')}
                </p>
              </div>
            </div>

            {/* Feature 4 */}
            <div className="relative scroll-animate group">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-400 to-pink-600 rounded-3xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-500"></div>
              <div className="relative bg-white rounded-3xl p-8 h-full transition-all duration-500 hover:scale-105 hover:shadow-2xl border border-gray-100">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center mb-6 shadow-lg shadow-purple-500/30 transform group-hover:rotate-6 transition-transform duration-500">
                  <DollarSign className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
                  {t('features.collection.title')}
                </h3>
                <p className="text-gray-600 text-base leading-relaxed">
                  {t('features.collection.desc')}
                </p>
              </div>
            </div>

            {/* Feature 5 */}
            <div className="relative scroll-animate group">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-400 to-purple-600 rounded-3xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-500"></div>
              <div className="relative bg-white rounded-3xl p-8 h-full transition-all duration-500 hover:scale-105 hover:shadow-2xl border border-gray-100">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mb-6 shadow-lg shadow-indigo-500/30 transform group-hover:rotate-6 transition-transform duration-500">
                  <FileText className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
                  {t('features.payroll.title')}
                </h3>
                <p className="text-gray-600 text-base leading-relaxed">
                  {t('features.payroll.desc')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Promo Banner */}
      <section className="relative bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 py-24 md:py-32 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-center relative z-10">
          {/* Background decorative icons */}
          <div className="absolute top-4 left-4 opacity-10 animate-pulse">
            <Route className="w-16 h-16 rotate-12 text-white" />
          </div>
          <div className="absolute top-8 right-8 opacity-10 animate-pulse" style={{animationDelay: '0.5s'}}>
            <Building2 className="w-20 h-20 -rotate-12 text-white" />
          </div>
          <div className="absolute bottom-6 left-12 opacity-10 animate-pulse" style={{animationDelay: '1s'}}>
            <Receipt className="w-16 h-16 rotate-45 text-white" />
          </div>
          <div className="absolute bottom-4 right-4 opacity-10 animate-pulse" style={{animationDelay: '1.5s'}}>
            <FileText className="w-14 h-14 -rotate-45 text-white" />
          </div>

          <div className="relative text-center z-10">
            <div className="flex justify-center mb-8">
              <div className="w-24 h-24 bg-white/20 backdrop-blur-md rounded-3xl border-2 border-white/30 flex items-center justify-center shadow-2xl transform hover:scale-110 transition-transform duration-300">
                <Briefcase className="w-12 h-12 text-white" />
              </div>
            </div>

            <h2 className="text-white font-extrabold text-3xl sm:text-4xl md:text-5xl mb-6">
              {t('promo.title')}
            </h2>
            <p className="text-white/90 mt-3 md:mt-4 text-lg sm:text-xl max-w-3xl mx-auto leading-relaxed">
              {t('promo.subtitle')}
            </p>

            {/* Feature highlights with icons */}
            <div className="flex flex-wrap justify-center gap-4 mt-10">
              <div className="group flex items-center gap-3 bg-white/10 backdrop-blur-md rounded-2xl px-6 py-3 border border-white/20 hover:bg-white/20 hover:border-white/40 transition-all duration-300 hover:scale-105">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center group-hover:bg-white/30 transition-colors">
                  <Route className="w-5 h-5 text-white" />
                </div>
                <span className="text-white text-sm font-semibold">{t('promo.liveTracking')}</span>
              </div>
              <div className="group flex items-center gap-3 bg-white/10 backdrop-blur-md rounded-2xl px-6 py-3 border border-white/20 hover:bg-white/20 hover:border-white/40 transition-all duration-300 hover:scale-105">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center group-hover:bg-white/30 transition-colors">
                  <Receipt className="w-5 h-5 text-white" />
                </div>
                <span className="text-white text-sm font-semibold">{t('promo.expenseManagement')}</span>
              </div>
              <div className="group flex items-center gap-3 bg-white/10 backdrop-blur-md rounded-2xl px-6 py-3 border border-white/20 hover:bg-white/20 hover:border-white/40 transition-all duration-300 hover:scale-105">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center group-hover:bg-white/30 transition-colors">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <span className="text-white text-sm font-semibold">{t('promo.payrollIntegration')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative bg-gradient-to-b from-white via-purple-50/20 to-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center scroll-animate mb-16">
            <div className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold px-6 py-3 shadow-lg shadow-purple-500/30 mb-6">
              <Star className="w-4 h-4 mr-2 fill-white" />
              {t('testimonials.badge')}
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('testimonials.title')}
            </h2>
            <p className="text-gray-600 text-xl max-w-3xl mx-auto leading-relaxed">
              {t('testimonials.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="relative scroll-animate group">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-purple-600 rounded-3xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-500"></div>
              <div className="relative bg-white rounded-3xl p-8 h-full transition-all duration-500 hover:scale-105 hover:shadow-2xl border border-gray-100">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />)}
                </div>
                <p className="text-gray-700 mb-6 text-sm md:text-base leading-relaxed">
                  {t('testimonials.t1.quote')}
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 border-2 border-black flex items-center justify-center">
                    <UserCheck className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-black">{t('testimonials.t1.name')}</div>
                    <div className="text-sm text-gray-600">{t('testimonials.t1.role')}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="relative scroll-animate group">
              <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-emerald-600 rounded-3xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-500"></div>
              <div className="relative bg-white rounded-3xl p-8 h-full transition-all duration-500 hover:scale-105 hover:shadow-2xl border border-gray-100">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />)}
                </div>
                <p className="text-gray-700 mb-6 text-sm md:text-base leading-relaxed">
                  {t('testimonials.t2.quote')}
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-green-600 border-2 border-black flex items-center justify-center">
                    <Receipt className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-black">{t('testimonials.t2.name')}</div>
                    <div className="text-sm text-gray-600">{t('testimonials.t2.role')}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="relative scroll-animate group">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-400 to-pink-600 rounded-3xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-500"></div>
              <div className="relative bg-white rounded-3xl p-8 h-full transition-all duration-500 hover:scale-105 hover:shadow-2xl border border-gray-100">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />)}
                </div>
                <p className="text-gray-700 mb-6 text-sm md:text-base leading-relaxed">
                  {t('testimonials.t3.quote')}
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 border-2 border-black flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-black">{t('testimonials.t3.name')}</div>
                    <div className="text-sm text-gray-600">{t('testimonials.t3.role')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="relative bg-gradient-to-br from-purple-50 to-white py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center scroll-animate mb-12">
            <div className="inline-flex items-center justify-center rounded-full bg-[#62329f] text-white font-semibold px-6 py-3 border-2 border-black shadow-[4px_4px_0_#000] mb-4">
              {t('faq.badge')}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
              {t('faq.title')}
            </h2>
            <p className="text-gray-600 text-lg">
              {t('faq.subtitle')}
            </p>
          </div>

          <div className="space-y-4">
            {[1, 2, 3, 4, 5, 6].map((num) => (
              <div key={num} className="relative scroll-animate">
                <div className="absolute inset-0 translate-x-2 translate-y-2 bg-[#62329f]/20 rounded-2xl"></div>
                <div className="relative bg-white rounded-2xl border-4 border-black p-6 md:p-8 transition-transform hover:-translate-x-1 hover:-translate-y-1">
                  <h3 className="text-lg md:text-xl font-bold text-black mb-3 flex items-start gap-3">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#62329f] text-white flex items-center justify-center text-sm font-bold">
                      {num}
                    </span>
                    <span>{t(`faq.q${num}.question`)}</span>
                  </h3>
                  <p className="text-gray-700 ml-11">
                    {t(`faq.q${num}.answer`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="relative bg-white py-12 md:py-20">
        <div className="w-full px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-start">
          {/* Left column */}
          <div className="text-left">
          <div className="inline-flex items-center justify-center rounded-full bg-[#62329f] text-white font-semibold px-6 py-3 border-2 border-black shadow-[4px_4px_0_#000] mb-4">
              {t('contact.badge')}
            </div>
            <p className="mt-4 md:mt-6 text-gray-800 text-base md:text-xl max-w-xl text-left">
              {t('contact.description')}
            </p>

            <div className="mt-12 md:mt-16">
              <div className="text-black font-medium mb-4">
                {t('contact.socialTitle')}
              </div>
              <div className="flex items-center gap-4">
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="w-10 h-10 border-2 border-black rounded-lg flex items-center justify-center hover:bg-[#34d77d]/20 transition-colors text-black"
                >
                  <img src="/instagram-logo.svg" alt="Instagram" className="w-5 h-5" loading="lazy" />
                </a>
                <a
                  href={FACEBOOK_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="w-10 h-10 border-2 border-black rounded-lg flex items-center justify-center hover:bg-[#34d77d]/20 transition-colors text-black"
                >
                  <img src="/facebook-logo.svg" alt="Facebook" className="w-5 h-5" loading="lazy" />
                </a>
                <a
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="w-10 h-10 border-2 border-black rounded-lg flex items-center justify-center hover:bg-[#34d77d]/20 transition-colors text-black"
                >
                  <img src="/linkedin-logo.svg" alt="LinkedIn" className="w-5 h-5" loading="lazy" />
                </a>
              </div>
            </div>
          </div>

          {/* Right column: form */}
          <div>
            <div className="rounded-2xl border-2 border-black p-4 md:p-8 shadow-[6px_6px_0_#000]">
              {submitted && (
                <div className="mb-4 md:mb-6 p-4 bg-green-100 border-2 border-green-400 text-green-700 rounded-xl">
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 mr-2" />
                    <span className="font-semibold">{t('contact.successMessage')}</span>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2 text-left">
                    {t('contact.nameLabel')}<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('contact.namePlaceholder')}
                    style={{ border: "2px solid #9ca3af" }}
                    className="w-full bg-white rounded-lg p-3 md:p-4 h-12 text-base text-gray-900 placeholder-gray-500 font-medium transition-all duration-200 hover:!border-gray-500 focus:outline-none focus:!border-primary-500 focus:ring-2 focus:ring-primary-500/20 hover:bg-gray-50 focus:bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2 text-left">
                    {t('contact.emailLabel')}<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('contact.emailPlaceholder')}
                    style={{ border: "2px solid #9ca3af" }}
                    className="w-full bg-white rounded-lg p-3 md:p-4 h-12 text-base text-gray-900 placeholder-gray-500 font-medium transition-all duration-200 hover:!border-gray-500 focus:outline-none focus:!border-primary-500 focus:ring-2 focus:ring-primary-500/20 hover:bg-gray-50 focus:bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2 text-left">
                    {t('contact.messageLabel')}<span className="text-red-500">*</span>
                  </label>
                  <textarea
                    rows="4"
                    required
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={t('contact.messagePlaceholder')}
                    style={{ border: "2px solid #9ca3af" }}
                    className="w-full bg-white rounded-lg p-3 md:p-4 resize-none text-gray-900 placeholder-gray-500 font-medium transition-all duration-200 hover:!border-gray-500 focus:outline-none focus:!border-primary-500 focus:ring-2 focus:ring-primary-500/20 hover:bg-gray-50 focus:bg-white"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className={`w-full rounded-xl h-12 text-base font-semibold transition-all duration-200 ${
                    loading
                      ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                      : 'bg-black text-white hover:bg-black/90'
                  }`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                      {t('contact.sending')}
                    </div>
                  ) : (
                    t('contact.sendMessage')
                  )}
                </Button>
              </form>
              <div className="mt-4 md:mt-6 border-t border-gray-300 pt-4 md:pt-6">
                <div className="space-y-2 md:space-y-3">
                  <a
                    href="https://www.trackteam.app"
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-3 text-black hover:underline"
                  >
                    <Link2 className="w-5 h-5" />
                    <span>www.trackteam.app</span>
                  </a>
                  <a
                    href="mailto:support@trackteam.app"
                    className="flex items-center gap-3 text-black hover:underline"
                  >
                    <Mail className="w-5 h-5" />
                    <span>support@trackteam.app</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Footer */}
      <Footer />
      <WhatsAppFloat />
      <FAQSchema />
    </div>
  );
}
